import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Typography, IconButton, Grid, TextField, MenuItem, Button, Autocomplete,
    Dialog, DialogTitle, DialogContent, CircularProgress, Paper
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { getAllPostMonthlyTargetsAction, createPostActivityAction, clearCreatePostActivityAction, getAllVillagesAction, getAllPostActivitiesAction, getPostActivityByIdAction, updatePostActivityAction, clearUpdatePostActivityAction, getAllDistrictsAction, getAllMandalsAction, createVillageAction, clearCreateVillageAction, createMandalAction, clearCreateMandalAction } from "../../../store/actions";

// ─── Create Village Dialog ───────────────────────────────────────────────────
const CreateVillageDialog = ({ open, onClose, onSuccess, userId }) => {
    const dispatch = useDispatch();

    const { successData: districtsData } = useSelector((s) => s.getAllDistrictsReducer || {});
    const { successData: mandalsData }   = useSelector((s) => s.getAllMandalsReducer || {});
    const { successData: createVillageSuccess, loading: createVillageLoading, error: createVillageError, errorInfo: createVillageErrorInfo } = useSelector((s) => s.createVillageReducer || {});
    const { successData: createMandalSuccess, loading: createMandalLoading, error: createMandalError, errorInfo: createMandalErrorInfo } = useSelector((s) => s.createMandalReducer || {});

    const [districtId, setDistrictId]   = useState('');
    const [mandalText, setMandalText]   = useState('');
    const [selectedMandalId, setSelectedMandalId] = useState('');
    const [mandalFocused, setMandalFocused]   = useState(false);
    const [villageName, setVillageName] = useState('');
    const [nameError,   setNameError]   = useState('');
    const [pendingVillageCreate, setPendingVillageCreate] = useState(false);

    const getMandalInchargeId = (m) =>
        m.inchargeUserId ?? m.incharge_user_id ?? m.mandalInchargeUserId ?? m.inchargeUser?.userId ?? null;

    const getMandalDistrictId = (m) =>
        m.districtId ?? m.district_id ?? m.districtID ?? null;

    const allMandals = useMemo(() => mandalsData?.data?.mandals || [], [mandalsData]);
    const allDistricts = useMemo(() => districtsData?.data?.districts || [], [districtsData]);

    const mandalSuggestions = useMemo(() =>
        districtId
            ? allMandals.filter(m => String(getMandalDistrictId(m)) === String(districtId))
            : []
    , [allMandals, districtId]);

    const matchingMandals = useMemo(() =>
        mandalText.trim()
            ? mandalSuggestions.filter(m =>
                (m.name || m.mandalName || m.mandal_name || '').toLowerCase().includes(mandalText.toLowerCase().trim())
              )
            : mandalSuggestions
    , [mandalSuggestions, mandalText]);

    const userMandals = useMemo(() => {
        if (!userId) return allMandals;
        const filtered = allMandals.filter(m => String(getMandalInchargeId(m)) === String(userId));
        return filtered.length > 0 ? filtered : allMandals;
    }, [allMandals, userId]);

    const userDistricts = useMemo(() => {
        const districtIds = new Set(
            userMandals.map(m => getMandalDistrictId(m)).filter(Boolean).map(String)
        );
        if (districtIds.size === 0) return allDistricts;
        const filtered = allDistricts.filter(d => districtIds.has(String(d.districtId)));
        return filtered.length > 0 ? filtered : allDistricts;
    }, [allDistricts, userMandals]);

    useEffect(() => {
        if (!open) return;
        if (userDistricts.length === 1) setDistrictId(String(userDistricts[0].districtId));
    }, [open, userDistricts]);

    useEffect(() => {
        setMandalText('');
        setSelectedMandalId('');
    }, [districtId]);

    useEffect(() => {
        if (open) {
            dispatch(getAllDistrictsAction());
            dispatch(getAllMandalsAction());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (!open) return;
        if (createMandalSuccess && pendingVillageCreate) {
            const newMandalId = createMandalSuccess?.data?.mandalId
                || createMandalSuccess?.data?.id
                || createMandalSuccess?.mandalId;
            if (newMandalId) {
                dispatch(clearCreateMandalAction());
                dispatch(getAllMandalsAction());
                dispatch(createVillageAction({ name: villageName.trim(), mandalId: Number(newMandalId) }));
                setPendingVillageCreate(false);
            }
        }
        if (createMandalError && pendingVillageCreate) {
            toast.error(createMandalErrorInfo || 'Failed to create mandal');
            dispatch(clearCreateMandalAction());
            setPendingVillageCreate(false);
        }
    }, [open, createMandalSuccess, createMandalError]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) return;
        if (createVillageSuccess) {
            toast.success('Village created successfully!');
            dispatch(clearCreateVillageAction());
            dispatch(getAllVillagesAction());
            const newId = createVillageSuccess?.data?.villageId || createVillageSuccess?.data?.id || createVillageSuccess?.villageId;
            onSuccess?.(newId);
            handleClose();
        }
        if (createVillageError) {
            toast.error(createVillageErrorInfo || 'Failed to create village');
            dispatch(clearCreateVillageAction());
        }
    }, [open, createVillageSuccess, createVillageError]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        setDistrictId(''); setMandalText(''); setSelectedMandalId('');
        setVillageName(''); setNameError(''); setPendingVillageCreate(false);
        onClose();
    };

    const handleSubmit = () => {
        if (!villageName.trim()) { setNameError('Village name is required'); return; }
        if (!mandalText.trim()) { toast.error('Please enter or select a mandal'); return; }
        if (!districtId)        { toast.error('Please select a district'); return; }
        setNameError('');
        if (selectedMandalId) {
            dispatch(createVillageAction({ name: villageName.trim(), mandalId: Number(selectedMandalId) }));
        } else {
            setPendingVillageCreate(true);
            dispatch(createMandalAction({ name: mandalText.trim(), districtId: Number(districtId), inchargeUserId: null }));
        }
    };

    const singleDistrict = userDistricts.length === 1;
    const loading = createVillageLoading || createMandalLoading;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
            slotProps={{ paper: { sx: { borderRadius: 4, p: { xs: 2, sm: 3 } } } }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 12, right: 12 }}>
                <CloseIcon />
            </IconButton>
            <DialogTitle sx={{ p: 0, mb: 2 }}>
                <Typography fontSize={22} fontWeight={700}>Create Village</Typography>
                <Typography fontSize={13} color="text.secondary">Fill in the details below</Typography>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* District */}
                <Box>
                    <Typography fontSize={13} fontWeight={600} mb={0.5}>District</Typography>
                    {singleDistrict ? (
                        <TextField
                            fullWidth size="small" value={userDistricts[0]?.name || ''}
                            disabled
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F3F4F6' } }}
                        />
                    ) : (
                        <Autocomplete
                            options={userDistricts}
                            getOptionLabel={(o) => o.name || ''}
                            isOptionEqualToValue={(o, v) => o.districtId === v.districtId}
                            value={userDistricts.find(d => String(d.districtId) === String(districtId)) || null}
                            onChange={(_, val) => { setDistrictId(val ? String(val.districtId) : ''); setMandalText(''); setSelectedMandalId(''); }}
                            renderInput={(params) => (
                                <TextField {...params} size="small" placeholder="Select district"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                            )}
                        />
                    )}
                </Box>

                {/* Mandal — search or create */}
                <Box>
                    <Typography fontSize={13} fontWeight={600} mb={0.5}>Mandal</Typography>
                    <Box sx={{ position: 'relative' }}>
                        <TextField
                            fullWidth size="small"
                            placeholder={districtId ? 'Search or create mandal' : 'Select district first'}
                            value={mandalText}
                            onChange={(e) => { setMandalText(e.target.value); setSelectedMandalId(''); }}
                            onFocus={() => setMandalFocused(true)}
                            onBlur={() => setTimeout(() => setMandalFocused(false), 200)}
                            disabled={!districtId}
                            helperText={
                                selectedMandalId
                                    ? '✓ Existing mandal selected'
                                    : mandalText.trim()
                                    ? 'New mandal will be created'
                                    : ''
                            }
                            FormHelperTextProps={{ sx: { color: selectedMandalId ? 'success.main' : 'info.main' } }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        {mandalFocused && matchingMandals.length > 0 && (
                            <Paper elevation={4} sx={{ position: 'absolute', zIndex: 1400, width: '100%', maxHeight: 160, overflow: 'auto', mt: 0.5, borderRadius: 1 }}>
                                {matchingMandals.map((m) => (
                                    <Box
                                        key={m.mandalId || m.id}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            const name = m.name || m.mandalName || m.mandal_name || '';
                                            setMandalText(name);
                                            setSelectedMandalId(String(m.mandalId || m.id));
                                            setMandalFocused(false);
                                        }}
                                        sx={{ py: 0.75, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F3F6F9' } }}
                                    >
                                        <Typography variant="body2">{m.name || m.mandalName || m.mandal_name || ''}</Typography>
                                    </Box>
                                ))}
                            </Paper>
                        )}
                    </Box>
                </Box>

                {/* Village Name */}
                <Box>
                    <Typography fontSize={13} fontWeight={600} mb={0.5}>Village Name</Typography>
                    <TextField
                        fullWidth size="small" placeholder="Enter village name"
                        value={villageName}
                        onChange={(e) => { setVillageName(e.target.value); if (nameError) setNameError(''); }}
                        error={Boolean(nameError)}
                        helperText={nameError}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Box>

                {/* Actions */}
                <Box display="flex" gap={2} mt={1}>
                    <Button fullWidth variant="outlined" onClick={handleClose} disabled={loading}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#000', border: '1px solid #000' }}>
                        Cancel
                    </Button>
                    <Button fullWidth variant="contained" onClick={handleSubmit} disabled={loading}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#5B4CF5', '&:hover': { bgcolor: '#4a3de0' } }}>
                        {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
// ─────────────────────────────────────────────────────────────────────────────

const SoActivity = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const editData = location.state?.editData || null;
    const autoPostTargetId = location.state?.postTargetId ?? null;
    const autoMonthId = location.state?.monthId ?? null;
    const [activityId, setActivityId] = useState(editData?.id || null);
    const [addVillageOpen, setAddVillageOpen] = useState(false);

    const userDetails = useMemo(() => {
        try { return JSON.parse(localStorage.getItem("userDetails")); }
        catch { return null; }
    }, []);

    // Media state for local files and captured images
    const [mediaFiles, setMediaFiles] = useState([]);

    // Redux selectors
    const { successData: monthlyTargetsData, loading: monthlyTargetsLoading, error: monthlyTargetsError } = useSelector(
        (state) => state.getAllPostMonthlyTargetsReducer || {}
    );
    const { successData: villagesData, loading: villagesLoading } = useSelector(
        (state) => state.getAllVillagesReducer || {}
    );
    const { successData: createActivitySuccess, error: createActivityError, loading: createActivityLoading } = useSelector(
        (state) => state.createPostActivityReducer || {}
    );
    const { successData: updateActivitySuccess, error: updateActivityError, loading: updateActivityLoading } = useSelector(
        (state) => state.updatePostActivityReducer || {}
    );
    const { successData: getActivityByIdData } = useSelector(
        (state) => state.getPostActivityByIdReducer || {}
    );

    // Parse data
    const monthlyTargetsList = useMemo(() => {
        const data = monthlyTargetsData?.data?.monthlyTargets
            || monthlyTargetsData?.data
            || monthlyTargetsData || [];
        return Array.isArray(data) ? data : [];
    }, [monthlyTargetsData]);
    const villagesList = useMemo(() => villagesData?.data?.villages || [], [villagesData]);

    // Get unique post_target_ids for dropdown
    const uniquePreTargets = useMemo(() => {
        const uniqueIds = [...new Set(monthlyTargetsList.map(mt => mt.post_target_id).filter(id => id != null))];
        const result = uniqueIds.map(id => ({
            post_target_id: id,
            label: `Post-Target ID: ${id}`
        }));
        return result;
    }, [monthlyTargetsList]);

    // Formik setup (updated initial values and fields)
    const formik = useFormik({
        initialValues: {
            post_target_id: '',
            post_month_id: '',
            village_id: '',
            activity_name: '',
            start_time: '',
            end_time: '',
            no_of_farmers: '',
        },
        enableReinitialize: true,
        onSubmit: (values) => {
            // Combine today's date with the selected time for the API
            const todayDate = new Date().toISOString().split('T')[0];
            const toDateTime = (t) => t ? `${todayDate}T${t}:00` : '';

            // Prepare FormData for API
            const formData = new FormData();
            formData.append("post_target_id", String(values.post_target_id));
            formData.append("post_month_id", String(values.post_month_id));
            formData.append("village_id", String(values.village_id));
            formData.append("activity_name", values.activity_name);
            formData.append("start_time", toDateTime(values.start_time));
            formData.append("end_time", toDateTime(values.end_time));
            formData.append("no_of_farmers", String(values.no_of_farmers));

            // Append each media file (actual file, not JSON)
            mediaFiles.forEach((fileObj, idx) => {
                if (fileObj.file) {
                    formData.append("media", fileObj.file, fileObj.name);
                }
            });

            if (activityId) {
                dispatch(updatePostActivityAction({ id: activityId, data: formData, endPoint: `activity/post/${activityId}` }));
            } else {
                dispatch(createPostActivityAction(formData));
            }
        },
        validate: () => ({}),
    });

    // Fetch data on mount
    useEffect(() => {
        dispatch(getAllPostMonthlyTargetsAction());
        dispatch(getAllVillagesAction());
    }, [dispatch]);

    // Fetch activity by id if editing
    useEffect(() => {
        if (editData?.id) {
            setActivityId(editData.id);
            dispatch(getPostActivityByIdAction({ endPoint: `activity/post/${editData.id}` }));
        }
    }, [editData, dispatch]);

    // Populate form when getActivityByIdData is loaded
    useEffect(() => {
        if (activityId && getActivityByIdData?.data) {
            const d = getActivityByIdData.data;
            // Extract HH:MM from a full datetime string (e.g. "2024-01-15T09:30:00")
            const toTime = (dt) => {
                if (!dt) return '';
                const t = dt.includes('T') ? dt.split('T')[1] : dt;
                return t.slice(0, 5);
            };
            formik.setValues({
                post_target_id: d.postTargetId || d.post_target_id || '',
                post_month_id: d.postMonthId || d.post_month_id || '',
                village_id: d.villageId || d.village_id || '',
                activity_name: d.activityName || d.activity_name || '',
                start_time: toTime(d.startTime || d.start_time),
                end_time: toTime(d.endTime || d.end_time),
                no_of_farmers: d.noOfFarmers || d.no_of_farmers || '',
            });
        }
    }, [activityId, getActivityByIdData]);


    // Handle create or update success
    useEffect(() => {
        if (createActivitySuccess) {
            toast.success("Activity created successfully!");
            dispatch(clearCreatePostActivityAction());
            dispatch(getAllPostActivitiesAction());
            navigate(-1);
        } else if (updateActivitySuccess) {
            toast.success("Activity updated successfully!");
            dispatch(clearUpdatePostActivityAction());
            dispatch(getAllPostActivitiesAction());
            navigate(-1);
        }
    }, [createActivitySuccess, updateActivitySuccess, dispatch, navigate]);


    // Handle create or update error
    useEffect(() => {
        if (createActivityError) {
            toast.error(createActivityError?.message || "Failed to create activity");
            dispatch(clearCreatePostActivityAction());
        } else if (updateActivityError) {
            toast.error(updateActivityError?.message || "Failed to update activity");
            dispatch(clearUpdatePostActivityAction());
        }
    }, [createActivityError, updateActivityError, dispatch]);

    // Auto-fill post_target_id and post_month_id when navigated from viewPostActivity
    useEffect(() => {
        if (!editData && autoPostTargetId && monthlyTargetsList.length > 0) {
            formik.setFieldValue('post_target_id', autoPostTargetId);
            if (autoMonthId) {
                formik.setFieldValue('post_month_id', autoMonthId);
            }
        }
    }, [autoPostTargetId, autoMonthId, monthlyTargetsList]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle monthly targets fetch error
    useEffect(() => {
        if (monthlyTargetsError) {
            toast.error("Failed to load monthly targets");
        }
    }, [monthlyTargetsError]);

    // Filter monthly targets by selected post_target_id
    const filteredMonthlyTargets = formik.values.post_target_id
        ? monthlyTargetsList.filter(mt => mt.post_target_id === Number(formik.values.post_target_id))
        : [];

    return (
        <Box sx={{ overflowX: 'hidden', width: '100%', boxSizing: 'border-box' }}>
            <Box
                sx={{
                    width: "100%",
                    background: "#FFFFFF",
                    borderRadius: 3,
                    p: { xs: 1.5, sm: 2 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    mt: { xs: 8, sm: 0, md: 0 },
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                }}
            >
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={3}>
                    <Typography
                        fontWeight={600}
                        sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2rem' }, flex: 1, minWidth: 0, wordBreak: 'break-word' }}
                    >
                        {editData ? "Edit Post-Season Activity" : "Post-Season Activity Information"}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconButton onClick={() => navigate(-1)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Form */}
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {/* Hide Post Target ID and Month fields if prefilled */}
                        {!(autoPostTargetId && autoMonthId) && (
                            <>
                                {/* Post Target ID */}
                                <Grid item size={{ xs: 12, sm: 4 }}>
                                    <Autocomplete
                                        options={uniquePreTargets}
                                        loading={monthlyTargetsLoading}
                                        readOnly={Boolean(!editData && autoPostTargetId)}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor: (!editData && autoPostTargetId) ? '#F3F4F6' : '#FAFAFA',
                                            },
                                            ...(!editData && autoPostTargetId ? {
                                                '& .MuiInputBase-input': { color: 'rgba(0,0,0,0.87)', WebkitTextFillColor: 'rgba(0,0,0,0.87)' },
                                            } : {}),
                                        }}
                                        value={uniquePreTargets.find(pt => pt.post_target_id === Number(formik.values.post_target_id)) || null}
                                        onChange={(e, val) => {
                                            formik.setFieldValue("post_target_id", val ? val.post_target_id : "");
                                            formik.setFieldValue("post_month_id", "");
                                        }}
                                        getOptionLabel={(option) => option.label || ""}
                                        isOptionEqualToValue={(option, value) => option.post_target_id === value.post_target_id}
                                        clearOnEscape
                                        noOptionsText={monthlyTargetsLoading ? "Loading..." : "No targets found"}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                label="Post Target ID"
                                                name="post_target_id"
                                                placeholder={monthlyTargetsLoading ? "Loading..." : "Select Post Target ID"}
                                                onBlur={() => formik.setFieldTouched("post_target_id", true)}
                                                error={formik.touched.post_target_id && Boolean(formik.errors.post_target_id)}
                                                helperText={formik.touched.post_target_id && formik.errors.post_target_id}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Post Month ID (show month_year as label, send id as value) */}
                                <Grid item size={{ xs: 12, sm: 4 }}>
                                    <Autocomplete
                                        options={filteredMonthlyTargets}
                                        loading={monthlyTargetsLoading}
                                        disabled={!formik.values.post_target_id}
                                        readOnly={Boolean(!editData && autoMonthId)}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor: (!editData && autoMonthId) ? '#F3F4F6' : '#FAFAFA',
                                            },
                                            ...(!editData && autoMonthId ? {
                                                '& .MuiInputBase-input': { color: 'rgba(0,0,0,0.87)', WebkitTextFillColor: 'rgba(0,0,0,0.87)' },
                                            } : {}),
                                        }}
                                        value={filteredMonthlyTargets.find(mt => String(mt.id) === String(formik.values.post_month_id)) || null}
                                        onChange={(e, val) => formik.setFieldValue("post_month_id", val ? val.id : "")}
                                        getOptionLabel={(option) => option.month_year || option.monthYear || ""}
                                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                        clearOnEscape
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                label="Month"
                                                name="post_month_id"
                                                placeholder={formik.values.post_target_id ? "Select Month" : "Select Post Target first"}
                                                onBlur={() => formik.setFieldTouched("post_month_id", true)}
                                                error={formik.touched.post_month_id && Boolean(formik.errors.post_month_id)}
                                                helperText={formik.touched.post_month_id && formik.errors.post_month_id}
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Village ID */}
                        <Grid item size={{ xs: 12, sm: 4 }}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                                <Autocomplete
                                    fullWidth
                                    options={villagesList}
                                    loading={villagesLoading}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor: "#FAFAFA",
                                        },
                                    }}
                                    value={villagesList.find(v => (v.villageId || v.id) === formik.values.village_id) || null}
                                    onChange={(e, val) => formik.setFieldValue("village_id", val ? (val.villageId || val.id) : "")}
                                    getOptionLabel={(option) => option.name || option.village_name || option.villageName || ""}
                                    isOptionEqualToValue={(option, value) => (option.villageId || option.id) === (value.villageId || value.id)}
                                    clearOnEscape
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            label="Village"
                                            name="village_id"
                                            onBlur={() => formik.setFieldTouched("village_id", true)}
                                            error={formik.touched.village_id && Boolean(formik.errors.village_id)}
                                            helperText={formik.touched.village_id && formik.errors.village_id}
                                        />
                                    )}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ minWidth: 36, px: 1, py: 0.9, fontWeight: 700, fontSize: 18, borderRadius: 2, flexShrink: 0, mt: 0.25 }}
                                    onClick={() => setAddVillageOpen(true)}
                                >
                                    +
                                </Button>
                            </Box>
                            <CreateVillageDialog
                                open={addVillageOpen}
                                onClose={() => setAddVillageOpen(false)}
                                userId={userDetails?.id}
                                onSuccess={(newVillageId) => {
                                    if (newVillageId) formik.setFieldValue('village_id', newVillageId);
                                    setAddVillageOpen(false);
                                }}
                            />
                        </Grid>

                        {/* Activity Name */}
                        <Grid item size={{ xs: 12, sm: 4 }}>
                            <TextField
                                size="small"
                                select
                                fullWidth
                                label="Activity Name"
                                name="activity_name"
                                value={formik.values.activity_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.activity_name && Boolean(formik.errors.activity_name)}
                                helperText={formik.touched.activity_name && formik.errors.activity_name}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        backgroundColor: "#FAFAFA",
                                    },
                                }}
                            >
                                <MenuItem value="">Select Activity</MenuItem>
                                <MenuItem value="DV">DV</MenuItem>
                                <MenuItem value="FV">FV</MenuItem>
                                <MenuItem value="FD">FD</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Start Time */}
                        <Grid item size={{ xs: 12, sm: 4 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Start Time"
                                name="start_time"
                                type="time"
                                value={formik.values.start_time}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.start_time && Boolean(formik.errors.start_time)}
                                helperText={formik.touched.start_time && formik.errors.start_time}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        backgroundColor: "#FAFAFA",
                                    },
                                }}
                            />
                        </Grid>

                        {/* End Time */}
                        <Grid item size={{ xs: 12, sm: 4 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="End Time"
                                name="end_time"
                                type="time"
                                value={formik.values.end_time}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.end_time && Boolean(formik.errors.end_time)}
                                helperText={formik.touched.end_time && formik.errors.end_time}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        backgroundColor: "#FAFAFA",
                                    },
                                }}
                            />
                        </Grid>

                        {/* Number of Farmers */}
                        <Grid item size={{ xs: 12, sm: 4 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Number of Farmers"
                                name="no_of_farmers"
                                value={formik.values.no_of_farmers}
                                onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*$/.test(value)) {   // allows only numbers
                                formik.setFieldValue("no_of_farmers", value);
                                }
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.no_of_farmers && Boolean(formik.errors.no_of_farmers)}
                                helperText={formik.touched.no_of_farmers && formik.errors.no_of_farmers}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        backgroundColor: "#FAFAFA",
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Image Upload & Capture */}
                    <Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={600} mb={1}>Upload or Capture Images</Typography>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                capture="environment"
                                style={{ display: 'block', marginBottom: 8 }}
                                onChange={e => {
                                    const files = Array.from(e.target.files);
                                    const fileObjs = files.map(file => ({
                                        file,
                                        name: file.name,
                                        url: URL.createObjectURL(file),
                                        captured_at: new Date().toISOString()
                                    }));
                                    setMediaFiles(prev => [...prev, ...fileObjs]);
                                }}
                            />
                            {/* Show preview gallery */}
                            <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                                {mediaFiles.map((file, idx) => (
                                    <Box key={idx} sx={{ position: "relative" }}>
                                        <img src={file.url} alt={file.name} style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 8 }} />
                                        <IconButton
                                            size="small"
                                            sx={{ position: "absolute", top: 0, right: 0, background: "rgba(255,255,255,0.7)" }}
                                            onClick={() => setMediaFiles(mediaFiles.filter((_, i) => i !== idx))}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Submit Buttons */}
                    <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(-1)}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                px: 3,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={formik.isSubmitting || !!createActivityLoading || !!updateActivityLoading}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                px: 3,
                            }}
                        >
                            {(createActivityLoading || updateActivityLoading)
                                ? (activityId ? "Updating..." : "Submitting...")
                                : (activityId ? "Update" : "Submit")}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Box>
    );
};

export default SoActivity;